import axios from "axios";
import {
    inPackingOrdersApiUrl,
    orderInvoiceLabelApiUrl,
    orderLabelApiUrl,
    proformaInvoiceApiUrl,
    proformaConvertToOrderApiUrl,
    b2bPackagingOrdersApiUrl,
    b2bOrderLabelApiUrl,
} from "../constants/apis";
import { getUserToken } from "../helpers/local_storage_helpers";

const getAuthConfig = () => ({
    headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${getUserToken()}`,
    },
});

const getBearerAuthConfig = () => ({
    headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getUserToken()}`,
    },
});

const getConfigWithHeaders = (authConfig, config = {}) => ({
    ...config,
    headers: {
        ...authConfig.headers,
        ...(config.headers || {}),
    },
});

const shouldRetryWithTokenAuth = (error) => (
    error?.response?.status === 401 || error?.response?.status === 403
);

// ── Shipping Labels ──

export const getOrderLabel = async (orderId, labelType) => {
    const response = await axios.get(
        `${orderLabelApiUrl}?order_id=${orderId}&label_type=${labelType}`,
        { ...getAuthConfig(), responseType: 'blob' }
    );
    return response;
};

export const createCustomLabel = async (data) => {
    const response = await axios.post(
        orderLabelApiUrl,
        data,
        { ...getAuthConfig(), responseType: 'blob' }
    );
    return response;
};

export const getInPackingOrders = async (params = {}, additionalConfig = {}) => {
    const config = { ...additionalConfig, params };
    try {
        const response = await axios.get(
            inPackingOrdersApiUrl,
            getConfigWithHeaders(getBearerAuthConfig(), config)
        );
        return response;
    } catch (error) {
        if (!shouldRetryWithTokenAuth(error)) throw error;
        const response = await axios.get(
            inPackingOrdersApiUrl,
            getConfigWithHeaders(getAuthConfig(), config)
        );
        return response;
    }
};

export const generateOrderInvoiceLabel = async (data) => {
    const config = { responseType: 'blob' };
    try {
        const response = await axios.post(
            orderInvoiceLabelApiUrl,
            data,
            getConfigWithHeaders(getBearerAuthConfig(), config)
        );
        return response;
    } catch (error) {
        if (!shouldRetryWithTokenAuth(error)) throw error;
        const response = await axios.post(
            orderInvoiceLabelApiUrl,
            data,
            getConfigWithHeaders(getAuthConfig(), config)
        );
        return response;
    }
};

// ── Proforma Invoices ──

// List endpoint is paginated: { count, next, previous, results }.
// `params` accepts { page, pageSize, search, createdBy }; orderId is the first arg.
export const getProformaInvoices = async (orderId, params = {}) => {
    const query = new URLSearchParams();
    if (orderId) query.set('order_id', orderId);
    if (params.page) query.set('page', params.page);
    if (params.pageSize) query.set('page_size', params.pageSize);
    if (params.search) query.set('search', params.search);
    if (params.createdBy) query.set('created_by', params.createdBy);
    const qs = query.toString();
    const url = qs ? `${proformaInvoiceApiUrl}?${qs}` : proformaInvoiceApiUrl;
    const response = await axios.get(url, getAuthConfig());
    return response;
};

export const getAllProformaInvoices = async (params = {}) => (
    getProformaInvoices(undefined, params)
);

export const createProformaInvoice = async (data) => {
    const response = await axios.post(
        proformaInvoiceApiUrl,
        data,
        getAuthConfig()
    );
    return response;
};

export const deleteProformaInvoice = async (proformaId) => {
    const response = await axios.delete(
        proformaInvoiceApiUrl,
        { ...getAuthConfig(), data: { proforma_id: proformaId } }
    );
    return response;
};

export const getProformaInvoiceForEdit = async (proformaId) => {
    const response = await axios.get(
        `${proformaInvoiceApiUrl}?proforma_id=${encodeURIComponent(proformaId)}`,
        getAuthConfig()
    );
    return response;
};

export const patchProformaInvoice = async (data) => {
    const response = await axios.patch(proformaInvoiceApiUrl, data, getAuthConfig());
    return response;
};

export const convertProformaToOrder = async ({
    proformaId,
    salesPerson,
    deliveryNote,
    transportName,
    zipcode,
    payments = [],
}) => {
    const hasImages = payments.some((p) => p.imageFile);

    let body;
    if (hasImages) {
        body = new FormData();
        body.append('proforma_id', proformaId);
        if (salesPerson) body.append('sales_person', salesPerson);
        if (deliveryNote) body.append('delivery_note', deliveryNote);
        if (transportName) body.append('transport_name', transportName);
        if (zipcode) body.append('zipcode', zipcode);
        if (payments.length) {
            const paymentsPayload = payments.map(({ imageFile, ...rest }, index) => {
                if (!imageFile) return rest;
                const imageFieldName = `payment_proof_${index}`;
                body.append(imageFieldName, imageFile);
                return { ...rest, image_field_name: imageFieldName };
            });
            body.append('payments', JSON.stringify(paymentsPayload));
        }
    } else {
        body = {
            proforma_id: proformaId,
            ...(salesPerson && { sales_person: salesPerson }),
            ...(deliveryNote && { delivery_note: deliveryNote }),
            ...(transportName && { transport_name: transportName }),
            ...(zipcode && { zipcode: zipcode }),
            ...(payments.length && { payments: payments.map(({ imageFile, ...rest }) => rest) }),
        };
    }

    const buildConfig = (authConfig) => hasImages
        ? { headers: { Authorization: authConfig.headers.Authorization } }
        : authConfig;

    try {
        const response = await axios.post(
            proformaConvertToOrderApiUrl,
            body,
            buildConfig(getBearerAuthConfig())
        );
        return response;
    } catch (error) {
        if (!shouldRetryWithTokenAuth(error)) throw error;
        const response = await axios.post(
            proformaConvertToOrderApiUrl,
            body,
            buildConfig(getAuthConfig())
        );
        return response;
    }
};

// ── B2B Shipping Labels ──

export const getB2bPackagingOrders = async (params = {}, additionalConfig = {}) => {
    const config = { ...additionalConfig, params };
    try {
        const response = await axios.get(
            b2bPackagingOrdersApiUrl,
            getConfigWithHeaders(getBearerAuthConfig(), config)
        );
        return response;
    } catch (error) {
        if (!shouldRetryWithTokenAuth(error)) throw error;
        const response = await axios.get(
            b2bPackagingOrdersApiUrl,
            getConfigWithHeaders(getAuthConfig(), config)
        );
        return response;
    }
};

export const generateB2bOrderLabel = async (payload) => {
    const token = getUserToken();
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`,
    };
    const res = await fetch(b2bOrderLabelApiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate PDF');
    }
    return await res.blob();
};

export const getB2bOrderLabelQuick = async (orderId) => {
    const token = getUserToken();
    const headers = {
        'Authorization': `Token ${token}`,
    };
    const res = await fetch(`${b2bOrderLabelApiUrl}?order_id=${encodeURIComponent(orderId)}`, {
        method: 'GET',
        headers,
    });
    if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate PDF');
    }
    return await res.blob();
};

